// ModalAperturaCaja.tsx — Co Marca — Apertura de Sesión POS
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Zap, DollarSign } from 'lucide-react';

interface ModalAperturaCajaProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (saldoInicial: number, observaciones: string) => void;
    isLoading: boolean;
    saldoActual?: number;
}

const C = {
    primary:        '#FF2052',
    primaryDark:    '#9B004A',
    secondary:      '#007FC8',
    secondaryDark:  '#004d7a',
    secondaryLight: '#00DBB9',
    accentDark:     '#EC8400',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
.mac-modal * { font-family:'DM Sans',sans-serif; box-sizing:border-box; }
@keyframes macFadeIn  { from{opacity:0;} to{opacity:1;} }
@keyframes macSlideUp { from{opacity:0;transform:translateY(20px) scale(.97);} to{opacity:1;transform:translateY(0) scale(1);} }
@keyframes macSpin { to{transform:rotate(360deg);} }
.mac-overlay { animation:macFadeIn .2s ease both; }
.mac-dialog  { display:flex; flex-direction:column; max-height:calc(100dvh - 32px); animation:macSlideUp .3s cubic-bezier(.16,1,.3,1) both; }
.mac-input {
    width:100%; padding:10px 14px; border-radius:10px;
    border:1.5px solid #e5e7eb; background:#fafafa;
    font-size:.88rem; color:#374151; outline:none; transition:all .2s;
    font-family:'DM Sans',sans-serif;
}
.mac-input:focus { border-color:${C.secondary}; box-shadow:0 0 0 3px ${C.secondary}12; background:#fff; }
.mac-textarea {
    width:100%; padding:10px 14px; border-radius:10px;
    border:1.5px solid #e5e7eb; background:#fafafa;
    font-size:.88rem; color:#374151; outline:none;
    transition:all .2s; resize:none; min-height:90px;
    font-family:'DM Sans',sans-serif;
}
.mac-textarea:focus { border-color:${C.secondary}; box-shadow:0 0 0 3px ${C.secondary}12; background:#fff; }
.mac-btn-confirm {
    display:inline-flex;align-items:center;justify-content:center;gap:7px;
    background:linear-gradient(135deg,${C.secondary},${C.secondaryDark});
    color:#fff;border:none;border-radius:11px;
    padding:10px 22px;font-size:.88rem;font-weight:800;
    cursor:pointer;box-shadow:0 4px 14px rgba(0,127,200,.28);
    transition:all .22s cubic-bezier(.16,1,.3,1);min-width:130px;
    font-family:'DM Sans',sans-serif;
}
.mac-btn-confirm:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 8px 22px rgba(0,127,200,.36); }
.mac-btn-confirm:disabled { background:#e2e8f0; color:#94a3b8; box-shadow:none; cursor:not-allowed; }
.mac-btn-ghost {
    display:inline-flex;align-items:center;gap:7px;
    background:#f8fafc;border:1.5px solid #e2e8f0;
    color:#374151;border-radius:11px;
    padding:10px 18px;font-size:.88rem;font-weight:700;
    cursor:pointer;transition:all .2s;
    font-family:'DM Sans',sans-serif;
}
.mac-btn-ghost:hover:not(:disabled) { background:#f1f5f9; }
.mac-btn-ghost:disabled { opacity:.5; cursor:not-allowed; }

.mac-body { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,1fr); gap:20px; overflow-y:auto; min-height:0; }
.mac-body > * { min-width:0; }
.mac-body > :first-child { grid-column:1; grid-row:1; }
.mac-body > :nth-child(2) { grid-column:2; grid-row:1 / span 2; }
.mac-body > :last-child { grid-column:1; grid-row:2; }
@media(max-width:640px) {
 .mac-body { grid-template-columns:minmax(0,1fr); gap:14px; }
 .mac-body > * { grid-column:1 !important; grid-row:auto !important; }
 .mac-footer { flex-wrap:wrap; }
 .mac-footer button { flex:1; justify-content:center; }
}
`;

const ModalAperturaCaja: React.FC<ModalAperturaCajaProps> = ({
    isOpen, onClose, onConfirm, isLoading, saldoActual = 0
}) => {
    const [saldoInicial, setSaldoInicial]   = useState(saldoActual.toFixed(2));
    const [observaciones, setObservaciones] = useState('');

    useEffect(() => {
        if (isOpen) setSaldoInicial(saldoActual.toFixed(2));
    }, [isOpen, saldoActual]);

    const handleConfirm = () => {
        onConfirm(parseFloat(saldoInicial), observaciones);
        setObservaciones('');
    };

    if (!isOpen) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <div className="mac-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                <div className="mac-overlay" onClick={() => { if (!isLoading) onClose(); }} style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.55)', backdropFilter:'blur(6px)' }}/>

                <div role="dialog" aria-modal="true" aria-labelledby="mac-title" className="mac-dialog" style={{ position:'relative', background:'#fff', borderRadius:'22px', width:'100%', maxWidth:'860px', boxShadow:'0 32px 80px rgba(0,0,0,.2)', border:'1.5px solid #f1f5f9', overflow:'hidden' }}>

                    {/* Barra top — gradiente Co Marca completo para apertura */}
                    <div style={{ height:'4px', background:`linear-gradient(90deg,${C.primary},${C.secondary},${C.accentDark},${C.secondaryLight})` }}/>

                    {/* Header */}
                    <div style={{ padding:'22px 24px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                            <div style={{ width:'40px', height:'40px', borderRadius:'13px', background:`linear-gradient(135deg,${C.secondary},${C.secondaryDark})`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, boxShadow:`0 4px 12px ${C.secondary}30` }}>
                                <Zap size={19} color="#fff"/>
                            </div>
                            <div>
                                <h3 id="mac-title" style={{ fontSize:'1.05rem', fontWeight:'900', color:'#0f172a', letterSpacing:'-.02em' }}>Abrir Nueva Sesión</h3>
                                <p style={{ fontSize:'.72rem', color:'#94a3b8', marginTop:'2px' }}>Ingresa el saldo inicial para comenzar</p>
                            </div>
                        </div>
                        <button onClick={onClose} disabled={isLoading}
                            style={{ width:'30px', height:'30px', borderRadius:'9px', border:'1.5px solid #f1f5f9', background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}
                            onMouseEnter={e=>(e.currentTarget as HTMLButtonElement).style.background='#f1f5f9'}
                            onMouseLeave={e=>(e.currentTarget as HTMLButtonElement).style.background='#f8fafc'}>
                            <X size={14} color="#64748b"/>
                        </button>
                    </div>

                    {/* Barra de colores decorativa */}
                    <div style={{ display:'flex', gap:'4px', padding:'12px 24px 0' }}>
                        {[C.secondary,C.secondaryLight,C.accentDark,C.primary].map((c,i)=>(
                            <div key={i} style={{ height:'2px', width:i===0?'16px':'5px', borderRadius:'2px', background:c }}/>
                        ))}
                    </div>

                    {/* Form */}
                    <div className="mac-body" style={{ padding:'20px 24px' }}>

                        {/* Saldo inicial */}
                        <div>
                            <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                <DollarSign size={11} color={C.secondary}/> Saldo Inicial (Bs.) *
                            </label>
                            <input type="number" value={saldoInicial} onChange={e=>setSaldoInicial(e.target.value)}
                                className="mac-input" placeholder="0.00" step="0.01" min="0" required/>
                            <p style={{ fontSize:'.68rem', color:'#94a3b8', marginTop:'5px' }}>Usa hasta 2 decimales para mayor precisión</p>
                            <div style={{ marginTop:'8px', padding:'10px 14px', background:`${C.secondaryLight}0a`, border:`1.5px solid ${C.secondaryLight}28`, borderRadius:'10px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                                <span style={{ fontSize:'.72rem', fontWeight:700, color:'#065f46' }}>Efectivo del cierre anterior</span>
                                <strong style={{ fontSize:'.9rem', color:'#0f172a' }}>{saldoActual.toFixed(2)} Bs.</strong>
                            </div>
                        </div>

                        {/* Observaciones */}
                        <div>
                            <label style={{ display:'block', fontSize:'.72rem', fontWeight:'800', color:'#374151', textTransform:'uppercase', letterSpacing:'.06em', marginBottom:'7px' }}>
                                Observaciones de apertura
                            </label>
                            <textarea value={observaciones} onChange={e=>setObservaciones(e.target.value)} className="mac-textarea"
                                placeholder="Observaciones sobre el estado inicial de la caja..."/>
                        </div>

                        {/* Info card */}
                        <div style={{ padding:'10px 14px', background:`${C.secondary}08`, border:`1.5px solid ${C.secondary}18`, borderRadius:'10px', display:'flex', alignItems:'flex-start', gap:'8px' }}>
                            <Zap size={13} color={C.secondary} style={{ flexShrink:0, marginTop:'2px' }}/>
                            <p style={{ fontSize:'.75rem', color:`${C.secondaryDark}`, lineHeight:1.55 }}>
                                Al iniciar la sesión se registrará el saldo inicial ingresado. Todos los movimientos quedarán asociados a esta sesión.
                            </p>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="mac-footer" style={{ padding:'14px 24px 22px', display:'flex', justifyContent:'flex-end', gap:'10px', borderTop:'1.5px solid #f8fafc', flexShrink:0 }}>
                        <button onClick={()=>{ onClose(); setObservaciones(''); }} disabled={isLoading} className="mac-btn-ghost">
                            Cancelar
                        </button>
                        <button onClick={handleConfirm} disabled={!saldoInicial||isLoading} className="mac-btn-confirm">
                            {isLoading
                                ? <><span style={{ width:'13px',height:'13px',border:'2px solid rgba(255,255,255,.4)',borderTopColor:'#fff',borderRadius:'50%',animation:'macSpin .7s linear infinite',display:'inline-block' }}/> Iniciando...</>
                                : <><Zap size={14}/> Iniciar Sesión</>
                            }
                        </button>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};

export default ModalAperturaCaja;
